import { useState, useEffect, useCallback, useRef } from 'react';

interface TokenState {
  token: string | null;
  loading: boolean;
  error: string | null;
  timestamp: number | null;
}

// Token expiration time in milliseconds (5 minutes)
const TOKEN_EXPIRATION_MS = 5 * 60 * 1000;

/**
 * Hook for managing HeyGen session tokens
 * Handles fetching, caching, and automatic cleanup of tokens
 */
export function useHeyGenToken() {
  const [state, setState] = useState<TokenState>({
    token: null,
    loading: false,
    error: null,
    timestamp: null,
  });
  const tokenTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup function to clear token
  const clearToken = useCallback(() => {
    setState((prev) => ({
      ...prev,
      token: null,
      timestamp: null,
    }));

    if (tokenTimerRef.current) {
      clearTimeout(tokenTimerRef.current);
      tokenTimerRef.current = null;
    }
  }, []);

  // Generate a new token
  const generateToken = useCallback(
    async (force: boolean = false) => {
      // Skip if already loading
      if (state.loading) return;

      // Skip if token exists, is valid, and not forced
      if (
        !force &&
        state.token &&
        state.timestamp &&
        Date.now() - state.timestamp < TOKEN_EXPIRATION_MS
      ) {
        console.log(
          '[useHeyGenToken] Token is still valid, returning cached token',
        );
        return state.token;
      }

      // Clear any existing timers
      if (tokenTimerRef.current) {
        clearTimeout(tokenTimerRef.current);
        tokenTimerRef.current = null;
      }

      // Start fetch process
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        console.log('[useHeyGenToken] Generating new session token');
        const response = await fetch(
          'https://api.heygen.com/v1/streaming.create_token',
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'content-type': 'application/json',
              'x-api-key': import.meta.env.VITE_HEYGEN_API_TOKEN,
            },
            body: JSON.stringify({}),
          },
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `Failed to get session token: ${response.status} ${errorText}`,
          );
        }

        const data = await response.json();
        const newToken = data.data.token;
        console.log('[useHeyGenToken] Successfully generated session token');

        // Update state with new token
        setState({
          token: newToken,
          loading: false,
          error: null,
          timestamp: Date.now(),
        });

        // Set expiration timer
        tokenTimerRef.current = setTimeout(() => {
          console.log('[useHeyGenToken] Token expired, clearing');
          clearToken();
        }, TOKEN_EXPIRATION_MS);

        return newToken;
      } catch (error) {
        console.error('[useHeyGenToken] Error generating token:', error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error:
            error instanceof Error ? error.message : 'Failed to generate token',
        }));
        return null;
      }
    },
    [state.loading, state.token, state.timestamp, clearToken],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tokenTimerRef.current) {
        clearTimeout(tokenTimerRef.current);
      }
    };
  }, []);

  return {
    token: state.token,
    loading: state.loading,
    error: state.error,
    generateToken,
    clearToken,
  };
}
